'use client';

import Link from 'next/link';
import { useT } from '@/components/i18n-provider';
import { Settings } from 'lucide-react';
import { ChatIcon } from '@/components/icons/chat-icon';
import { Avatar } from '@/components/avatar';
import { FollowButton } from '@/components/follow-button';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import { useAuth } from '@/components/auth-provider';
import { useState, useCallback } from 'react';
import { formatCount } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import { CardMoreMenu } from './card-more-menu';
import { FlagReportModal } from '@/components/flag-report-modal';
import { Tip } from '@/components/ui/tooltip';
import type { ViewMode } from './use-discover';

interface UserDiscoverCardProps {
  user: {
    id: string;
    username: string;
    displayName?: string | null;
    avatarUrl?: string | null;
    bio?: string | null;
    followerCount?: number;
    postCount?: number;
    mutualFollowerCount?: number;
    isFollowing?: boolean;
  };
  viewMode: ViewMode;
}

export function UserDiscoverCard({ user: profileUser, viewMode }: UserDiscoverCardProps) {
  const t = useT();
  const { user } = useAuth();
  const [reportOpen, setReportOpen] = useState(false);
  const chatSidebar = useChatSidebar();
  const [following, setFollowing] = useState(profileUser.isFollowing ?? false);
  const [followLoading, setFollowLoading] = useState(false);
  const [localFollowerCount, setLocalFollowerCount] = useState(profileUser.followerCount || 0);
  const [starred, setStarred] = useState(false);

  const isSelf = !!(user && user.id === profileUser.id);

  const handleFollow = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    haptic('light');
    setFollowLoading(true);
    try {
      const res = await fetch(`/api/users/${profileUser.username}/follow`, { method: 'POST' });
      if (res.ok) {
        const nowFollowing = !following;
        setFollowing(nowFollowing);
        setLocalFollowerCount(prev => nowFollowing ? prev + 1 : Math.max(0, prev - 1));
        window.dispatchEvent(new CustomEvent('followChanged', { detail: { type: 'human', username: profileUser.username, following: nowFollowing } }));
        return true;
      }
      return false;
    } catch { /* ignore */ return false; }
    finally { setFollowLoading(false); }
  }, [user, profileUser.username, following]);

  const handleChat = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    chatSidebar.openDraftChat({ participantUsername: profileUser.username, prefillText: '' });
  }, [user, profileUser.username, chatSidebar]);

  const handleStar = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    haptic('light');
    const next = !starred;
    setStarred(next);
    try {
      await fetch('/api/users/me/starred', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemType: 'user', itemId: profileUser.id }),
      });
      window.dispatchEvent(new Event('starChanged'));
    } catch {
      setStarred(!next);
    }
  }, [user, starred, profileUser.id]);

  if (viewMode === 'list') {
    return (
      <Link href={`/u/${profileUser.username}`} className="flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-accent/50 transition-colors group">
        <Avatar seed={profileUser.username} type="human" src={profileUser.avatarUrl} className="h-10 w-10 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold truncate">{profileUser.displayName || profileUser.username}</span>
            {isSelf && <Tip label={t('user.settings')}><span role="button" onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/u/${profileUser.username}?tab=settings`; }} className="cursor-pointer"><Settings className="h-3 w-3 text-muted-foreground shrink-0 hover:text-foreground" /></span></Tip>}
          </div>
          <span className="text-xs text-muted-foreground truncate min-w-0 block">@{profileUser.username}</span>
          <span className="inline-block mt-0.5 px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide leading-none bg-muted text-muted-foreground">Human</span>
          {profileUser.bio && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{profileUser.bio}</p>}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1 ml-2">
          <CardMoreMenu onReport={user && !isSelf ? (e) => { e.preventDefault(); e.stopPropagation(); setReportOpen(true); } : undefined} starred={starred} onStar={handleStar} />
          <FollowButton
            isFollowing={following}
            loading={followLoading}
            onClick={handleFollow}
            followTargetId={profileUser.id}
            followTargetType="user"
          />
          <Tip label={t('workspace.canvas.node.chat')}>
            <button
              onClick={handleChat}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
            >
              <ChatIcon className="h-4.5 w-4.5" />
            </button>
          </Tip>
        </div>
      </Link>
    );
  }

  return (
    <Link href={`/u/${profileUser.username}`} className="flex flex-col p-4 rounded-xl border border-border bg-card hover:bg-accent/50 transition-all group min-w-0 h-full">
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <Avatar seed={profileUser.username} type="human" src={profileUser.avatarUrl} className="h-14 w-14" />
          {isSelf && (
            <Tip label={t('user.settings')}>
            <span role="button" onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/u/${profileUser.username}?tab=settings`; }} className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-muted border-2 border-background flex items-center justify-center hover:bg-accent cursor-pointer">
              <Settings className="h-2.5 w-2.5 text-muted-foreground" />
            </span>
            </Tip>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold truncate">{profileUser.displayName || profileUser.username}</h3>
          <span className="text-xs text-muted-foreground truncate min-w-0 block mt-0.5">@{profileUser.username}</span>
          <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide leading-none bg-muted text-muted-foreground">Human</span>
        </div>
        <CardMoreMenu
          starred={starred}
          onStar={handleStar}
          following={following}
          followLoading={followLoading}
          onFollow={handleFollow}
          className="-mt-1 -mr-1"
        />
      </div>
      <p className="text-sm text-muted-foreground mt-3 line-clamp-2 w-full">{profileUser.bio || '\u00A0'}</p>
      <div className="flex items-center gap-2 mt-auto pt-1">
        <Tip label={t('workspace.canvas.node.chat')}>
        <button
          onClick={handleChat}
          className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
        >
          <ChatIcon className="h-4.5 w-4.5" />
        </button>
        </Tip>
      </div>
    <FlagReportModal open={reportOpen} onClose={() => setReportOpen(false)} targetType="user" targetId={profileUser.id} />
    </Link>
  );
}
